import {
  ChartPie,
  Fire,
  GlobeHemisphereWest,
  Link as LinkIcon,
  SealCheck,
} from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";

import { NotEnoughSeats } from "features/subscriptions/components";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import {
  ColumnsVisibilityFilter,
  PaginatedDataTable,
} from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useAvailableLicensesCount from "common/hooks/useAvailableLicensesCount";
import type { WorkspaceAccount } from "common/types";

import { MANAGE_ACCOUNTS_ACTIONS } from "../../constants";
import AccountActionsDropdown from "../AccountActionsDropdown";
import AccountName from "../AccountName";
import AccountProfileImage from "../AccountProfileImage";
import AccountProxyTag from "../AccountTags/AccountProxyTag";
import AccountSsiTag from "../AccountTags/AccountSsiTag";
import LicenseTag from "../AccountTags/LicenseTag";
import LoginStatusTag from "../AccountTags/LoginStatusTag";
import WarmupState from "../AccountTags/WarmupState";
import AssignSeats from "../ManageAccounts/AssignSeats";
import ManageInteractions from "../ManageAccounts/ManageInteractions";
import UpdateProxies from "../ManageAccounts/UpdateProxies";
import DesktopQuickAction from "./DesktopQuickAction";

import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

interface DesktopTableProps {
  accounts: WorkspaceAccount[];
  selectedAccounts: WorkspaceAccount[];
  setSelectedAccounts: (selectedAccounts: WorkspaceAccount[]) => void;
  filtersComponent: ReactNode;
}

const ACCOUNTS_DESKTOP_COLUMNS: ColumnDef<WorkspaceAccount>[] = [
  {
    id: "account",
    header: "Account",
    cell: ({ row: { original } }) => {
      const { occupation } = original;

      return (
        <div className="flex items-center gap-x-2.5">
          <AccountProfileImage account={original} />
          <div className="flex flex-col gap-y-1">
            <AccountName account={original} className="text-md-bold" />
            <span className="line-clamp-1 break-all text-xs-medium text-black-500">
              {occupation}
            </span>
          </div>
        </div>
      );
    },
    enableHiding: false,
  },
  {
    id: "warmup",
    header: "Warmup",
    cell: ({ row: { original } }) => (
      <WarmupState accountId={original.id} isTag={false} />
    ),
    meta: { icon: Fire, className: "min-w-28 max-w-40" },
  },
  {
    id: "proxy",
    header: "Proxy",
    cell: ({ row: { original } }) => (
      <AccountProxyTag size="md" account={original} />
    ),
    meta: { icon: GlobeHemisphereWest, className: "min-w-40 max-w-40" },
  },
  {
    id: "ssi",
    header: "SSI",
    cell: ({ row: { original } }) => (
      <AccountSsiTag account={original} isTag={false} />
    ),
    meta: { icon: ChartPie, className: "min-w-28 max-w-40" },
  },
  {
    id: "seat",
    header: "Seat",
    cell: ({
      row: {
        original: { type, license },
      },
    }) => (
      <LicenseTag isRented={type === "rental"} license={license} size="md" />
    ),
    meta: { icon: SealCheck, className: "min-w-40 max-w-40" },
  },
  {
    id: "status",
    header: "Login status",
    cell: ({ row: { original } }) => (
      <LoginStatusTag account={original} size="md" />
    ),
    meta: { icon: LinkIcon, className: "min-w-40 max-w-40" },
  },
  {
    id: "action",
    cell: ({ row: { original } }) => (
      <div className="inline-flex gap-x-2.5">
        <DesktopQuickAction account={original} />
        <AccountActionsDropdown account={original} />
      </div>
    ),
    enableHiding: false,
    meta: { className: "w-0", isSticky: true },
  },
];

export default function DesktopTable({
  accounts,
  selectedAccounts,
  setSelectedAccounts,
  filtersComponent,
}: DesktopTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useSelectedWorkspaceContext();
  const { availableLicensesCount } = useAvailableLicensesCount();

  function onRowClick(account: WorkspaceAccount) {
    navigate({
      pathname: account.id,
      search: location.search,
    });
  }

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "accounts",
      selectedRows: selectedAccounts,
      onRowSelectionChange: permissions.includes("accounts_full")
        ? setSelectedAccounts
        : undefined,
    },
    reactTableOptions: { data: accounts, columns: ACCOUNTS_DESKTOP_COLUMNS },
    isLoading: false,
  });

  return (
    <>
      <div className="flex flex-row items-center gap-x-1.5">
        {filtersComponent}
        <ColumnsVisibilityFilter table={table} />
      </div>

      <PaginatedDataTable
        title="Accounts"
        table={table}
        onRowClick={onRowClick}
        isLoading={false}
        selectionActionsComponent={
          <BulkManageDesktop
            actions={MANAGE_ACCOUNTS_ACTIONS}
            selection={selectedAccounts}
            renderActionComponent={({ action, onClose }) => {
              const noLicenseAccounts = selectedAccounts.filter(
                ({ license, type }) => type === "user" && !license,
              );
              if (
                action === "seats" &&
                availableLicensesCount < noLicenseAccounts.length
              ) {
                return (
                  <NotEnoughSeats
                    noSeatAccountIds={noLicenseAccounts.map(({ id }) => id)}
                    mixpanelSource="upsell_seat"
                  />
                );
              }
              const props = {
                selectedAccounts,
                onBack: onClose,
                onSuccess: () => {
                  onClose();
                  setSelectedAccounts([]);
                },
              };
              if (action === "proxy") {
                return <UpdateProxies {...props} />;
              }
              if (action === "interactions") {
                return <ManageInteractions {...props} />;
              }
              if (action === "seats") {
                return <AssignSeats {...props} />;
              }
            }}
          />
        }
      />
    </>
  );
}
